import { defineComponent, computed, reactive, ref, SetupContext } from '@vue/composition-api';
import { TableHeader, ProcessingStatusDetailList, OnColumns, ConfigIconCoordinate } from '@/shared/type';
import SystemConst from '@/shared/system-const';
import vueRouter from '@/router/index';                                                     // ルート情報取得
import HttpStatus from 'http-status-codes';                                                 // HTTPステータスコード取得用
import CommonFunction from '@/shared/common-function';                                      // 共通関数定義
import CommonOnColumnsMenu from '@/components/common/common-on-columns-menu.component.vue'; // 共通列設定メニューコンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';  // メッセージダイアログコンポーネント
import { DataOptions } from 'vuetify';                                                      // v-data-tableのupdateイベントの引数を利用するための型
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';      // フィルタ用共通コンポーネント
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    processingStatusDetailList: ProcessingStatusDetailList[];
    totalCount: number;
    isLoading: boolean;
    processingStatusDisplayTableHeaders: TableHeader[];
    filterDefs: TableFilterDefs;                           // フィルタ定義
    pageOptions: DataOptions;                              // ページ情報・ソート情報
    filters: TableFilters;                                 // フィルタ情報
    useFilter: boolean;                                    // フィルタが操作されたか
};

/**
 *  処理状況詳細コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonOnColumnsMenu, CommonTableFilter },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.PROCESSING_STATUS_USERS_DETAIL;  // テーブル高さ調整用変数
        const router = vueRouter;
        const path = computed(() => ctx.root.$route.params.processId);                // 画面遷移元からprocessId取得
        const tableHeaderClass: string[] = ['secondary subtitle-2'];                  // 処理状況一覧(詳細)のヘッダ色
        const page = ref<number>(1);                                                  // ページ情報
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                         // 1ページあたりの行数
        const itemsPerPage = ref<number>(linesPerPage[1]);                            // 1ページあたりの行数（初期値）
        const sortBy = ref<string[]>([]);                                             // ソートカラム
        const sortDesc = ref<boolean[]>([]);                                          // ソート昇順、降順
        const child = ref<any>();                                                     // テーブルフィルタ一覧
        const commonRef = ref<any>(null);                                             // メッセージダイアログ
        const columnRef = ref<any>(null);                                             // 列設定    
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                      // テーブルのデータ取得中に表示するテキスト
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            processingStatusDetailList: [],                                           // 処理状況詳細の一覧
            totalCount: 0,                                                            // 処理状況詳細の総件数
            isLoading: false,                                                         // テーブルがロード中: true ロード中でない: false
            processingStatusDisplayTableHeaders: [],                                  // 表示するテーブルヘッダー
            filterDefs: {                                                             // フィルタ定義
                'userId': { title: 'ユーザーID', types: 'KeyWordPartialMatch' },
                'identificationCode': { title: '識別コード', types: 'KeyWordPartialMatch' },
                'accountDivision': { title: 'アカウント区分', types: 'CheckBox' },
                'userName': { title: '氏名', types: 'KeyWordPartialMatch' },
                'userNameKana': { title: '氏名（かな）', types: 'KeyWordPartialMatch' },
                'organization': { title: '所属組織', types: 'PullDown' },
                'mail': { title: 'メールアドレス', types: 'KeyWordPartialMatch' },
                'postName': { title: '職', types: 'KeyWordPartialMatch' },
                'fiscalYear': { title: '作成年度', types: 'PullDown' },
                'isEnabled': { title: '状態', types: 'CheckBox' },
                'processingContent': { title: '処理内容', types: 'CheckBox' },
                'processingStatus': { title: '処理状況', types: 'CheckBox' },
                'executionResult': { title: '実行結果', types: 'KeyWordPartialMatch' }
            },
            pageOptions: {                                                            // ページ情報・ソート情報
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filters: {},                                                              // フィルタ情報
            useFilter: false                                                          // フィルタが操作されたか
        });
        const errorIcon: TableHeader[] = [
            // エラーアイコンヘッダ
            {
                text: '',
                value: 'isError',
                sortable: false,
                width: '40px',
                class: tableHeaderClass
            }
        ];
        const processingStatusInitialDisplayTableHeaders: TableHeader[] = [
            // 処理状況(詳細)ヘッダ情報(初期表示)
            // 識別コード
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '130px',
                class: tableHeaderClass
            },
            // アカウント区分
            {
                text: 'アカウント区分',
                value: 'accountDivision',
                width: '180px',
                class: tableHeaderClass
            },
            // 氏名
            {
                text: '氏名',
                value: 'userName',
                width: '180px',
                class: tableHeaderClass
            },
            // メールアドレス
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '290px',
                class: tableHeaderClass
            },
            // 作成年度
            {
                text: '作成年度',
                value: 'fiscalYear',
                width: '110px',
                class: tableHeaderClass
            },
            // 状態
            {
                text: '状態',
                value: 'isEnabled',
                width: '110px',
                class: tableHeaderClass
            },
            // 処理内容
            {
                text: '処理内容',
                value: 'processingContent',
                width: '110px',
                class: tableHeaderClass
            },
            // 処理状況
            {
                text: '処理状況',
                value: 'processingStatus',
                width: '130px',
                class: tableHeaderClass
            },
            // 処理結果
            {
                text: '実行結果',
                value: 'executionResult',
                width: '250px',
                class: tableHeaderClass
            },
            // 列設定アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        const processingStatusTableHeaders: TableHeader[] = [
            // 処理状況(詳細)ヘッダ情報(全項目)
            // ユーザーID
            {
                text: 'ユーザーID',
                value: 'userId',
                width: '130px',
                class: tableHeaderClass
            },
            // 識別コード
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '130px',
                class: tableHeaderClass
            },
            // アカウント区分
            {
                text: 'アカウント区分',
                value: 'accountDivision',
                width: '180px',
                class: tableHeaderClass
            },
            // 氏名
            {
                text: '氏名',
                value: 'userName',
                width: '180px',
                class: tableHeaderClass
            },
            // 氏名（かな）
            {
                text: '氏名（かな）',
                value: 'userNameKana',
                width: '180px',
                class: tableHeaderClass
            },
            // 所属組織
            {
                text: '所属組織',
                value: 'organization',
                width: '180px',
                class: tableHeaderClass
            },
            // メールアドレス
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '290px',
                class: tableHeaderClass
            },
            // 職
            {
                text: '職',
                value: 'postName',
                width: '110px',
                class: tableHeaderClass
            },
            // 作成年度
            {
                text: '作成年度',
                value: 'fiscalYear',
                width: '110px',
                class: tableHeaderClass
            },
            // 状態
            {
                text: '状態',
                value: 'isEnabled',
                width: '110px',
                class: tableHeaderClass
            },
            // 処理内容
            {
                text: '処理内容',
                value: 'processingContent',
                width: '110px',
                class: tableHeaderClass
            },
            // 処理状況
            {
                text: '処理状況',
                value: 'processingStatus',
                width: '130px',
                class: tableHeaderClass
            },
            // 処理結果
            {
                text: '実行結果',
                value: 'executionResult',
                width: '250px',
                class: tableHeaderClass
            },
            // 列設定アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // 列設定情報
        const onColumns: OnColumns[] = [
            {
                text: '全て表示',
                value: false
            },
            {
                text: 'ユーザーID',
                value: false
            },
            {
                text: '識別コード',
                value: true
            },
            {
                text: 'アカウント区分',
                value: true
            },
            {
                text: '氏名',
                value: true
            },
            {
                text: '氏名（かな）',
                value: false
            },
            {
                text: '所属組織',
                value: false
            },
            {
                text: 'メールアドレス',
                value: true
            },
            {
                text: '職',
                value: false
            },
            {
                text: '作成年度',
                value: true
            },
            {
                text: '状態',
                value: true
            },
            {
                text: '処理内容',
                value: true
            },
            {
                text: '処理状況',
                value: true
            },
            {
                text: '実行結果',
                value: true
            },
        ];

        // フィルタのバリデーション
        // ユーザーID
        const userIdRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, 'ユーザーID')
        ];
        // 識別コード
        const identificationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '識別コード')
        ];
        // 氏名
        const nameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '氏名')
        ];
        // 氏名（かな）
        const nameKanaRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '氏名（かな）')
        ];
        // メールアドレス
        const mailRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, 'メールアドレス')
        ];
        // 職
        const postNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '職')
        ];
        // 実行結果
        const executionResultRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '実行結果')
        ];
        // フィルターに設定された最大桁数
        const maxLength = SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH;

        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-list-status');

            // タイトル表示
            ctx.root.$store.commit('setMainTitle', '処理状況詳細');

            // 初期表示ヘッダーを設定
            state.processingStatusDisplayTableHeaders = errorIcon.concat(processingStatusInitialDisplayTableHeaders);

            // フィルタの初期設定を行う
            // 初期設定が完了するまでawaitで待つ
            await initFilter();
        }

        /**
         * フィルタ初期設定
         * 
         * フィルタにDBから取得する必要のある条件がある場合、初期表示で取得する。
         */
        async function initFilter() {
            // フィルタ条件の内容を取得
            // ユーザー処理状況管理ストアのgetDetailFilterConditionsを呼び出す
            const response = await ctx.root.$store.dispatch('processingStatusUsers/getDetailFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // フィルタ（プルダウン・チェックボックス）に表示するデータを設定する
            // アカウント区分
            state.filterDefs['accountDivision'].chkItems = response.data.accountDivision;
            // 所属組織
            state.filterDefs['organization'].items = response.data.organizations;
            // 作成年度
            state.filterDefs['fiscalYear'].items = response.data.fiscalYear;
            // 状態
            state.filterDefs['isEnabled'].chkItems = response.data.isEnabled;
            // 処理内容
            state.filterDefs['processingContent'].chkItems = response.data.processingContent;
            // 処理状況
            state.filterDefs['processingStatus'].chkItems = response.data.processingStatus;

            // バリデーションを設定する
            // ユーザーID
            state.filterDefs['userId'].validator = userIdRules;
            // 識別コード
            state.filterDefs['identificationCode'].validator = identificationCodeRules;
            // 氏名
            state.filterDefs['userName'].validator = nameRules;
            // 氏名（かな）
            state.filterDefs['userNameKana'].validator = nameKanaRules;
            // メールアドレス
            state.filterDefs['mail'].validator = mailRules;
            // 職
            state.filterDefs['postName'].validator = postNameRules;
            // 実行結果
            state.filterDefs['executionResult'].validator = executionResultRules;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // フィルタ操作フラグをtrueにする
            state.useFilter = true;

            // 処理状況詳細を更新する
            updateList();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * 
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            // ページ情報を設定する
            page.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソート情報を設定する
            // ソート情報を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 処理状況詳細を表示更新する
            updateList();
        }

        /**
         * 処理状況詳細の表示更新
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // 処理状況詳細情報をクリア
            state.processingStatusDetailList = [];

            // 総検索件数をクリア
            state.totalCount = 0;

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // アカウント区分フィルタで「教職員（組織管理者）」が選択されている場合、フィルタパラメータを調整する（paramsは参照渡し）
            CommonFunction.setOrgManagerFilterParams(params, state.filters);

            // 処理状況一覧取得APIでデータを取得する
            // GET /api/processing-status-users/{processId}
            // パラメータ: 中間処理ID
            //            作成したパラメータ
            const response = await ctx.root.$store.dispatch('processingStatusUsers/getProcessDetails',
                {
                    processId: path.value,
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する。
                state.isLoading = false;

                // HTTPステータスコードが404の場合、"指定された処理が存在しない"
                // 旨のメッセージを表示して親画面へ遷移する。
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 初期表示時の404エラーのみダイアログを表示する
                    if (!state.useFilter) {
                        commonRef.value.displayErrorMessageTransitionDialog(response, '/processing-status-users/list');
                    }
                } else if (response.status === HttpStatus.BAD_REQUEST) {
                    // HTTPステータスコードが400はエラーダイアログを表示して一覧に戻る。
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/processing-status-users/list');
                } else {
                    // HTTPステータスコードが400, 404以外はエラー画面へ遷移して処理を終了する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // 処理状況一覧のデータをstate.processingStatusDetailListへ設定する
            state.processingStatusDetailList = response.data.processingStatusUserDetails;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            router.push('/processing-status-users/list');
        }

        /**
         * 列設定アイコンをクリックした際に発火する
         * 列設定アイコンの座標を取得し、列設定メニューコンポーネントのshowMenuを発火する
         */
        function showMenu() {
            // 列設定アイコンの座標を取得
            const configIconCoordinate: ConfigIconCoordinate = { x: null, y: null };
            configIconCoordinate.x = document.querySelector('#column');
            configIconCoordinate.y = document.querySelector('#column');

            // 列設定メニューコンポーネントのshowMenuを発火
            columnRef.value.showMenu(configIconCoordinate);
        }

        /**
         * 列設定メニューで、列設定情報が更新された際に発火する
         * 設定後のテーブルヘッダー情報を取得する
         * 
         * @param displayTableHeaders 画面に表示するテーブルヘッダー情報
         */
        function getTableHeader(displayTableHeaders: TableHeader[]) {
            // ヘッダー情報の更新
            state.processingStatusDisplayTableHeaders = errorIcon.concat(displayTableHeaders);
        }

        return {
            tableHeight,
            state,
            page,
            linesPerPage,
            itemsPerPage,
            sortBy,
            sortDesc,
            child,
            commonRef,
            columnRef,
            tableLoadingText,
            errorIcon,
            processingStatusInitialDisplayTableHeaders,
            processingStatusTableHeaders,
            onColumns,
            updateFilter,
            updateOptions,
            updateList,
            onResize,
            back,
            showMenu,
            getTableHeader
        };
    }
});
